import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    this.style.top = OFFSET + 'px';

    // Mega-panels: left-align to the facets filters wrapper instead of the trigger
    if (this.classList.contains('facets__panel-content--mega')) {
      const filtersWrapper = this.closest('.facets__filters-wrapper') || this.closest('.facets--horizontal');
      if (filtersWrapper) {
        const wrapperRect = filtersWrapper.getBoundingClientRect();
        const panelParent = this.offsetParent || this.parentElement;
        const parentRect = panelParent ? panelParent.getBoundingClientRect() : wrapperRect;
        this.style.left = `${wrapperRect.left - parentRect.left}px`;

        // Check for right overflow after positioning
        const rect = this.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        if (rect.right > viewportWidth) {
          const overflowAmount = rect.right - viewportWidth + OFFSET;
          this.style.left = `${parseFloat(this.style.left) - overflowAmount}px`;
        }
      }
      this.#mutationObserver.takeRecords();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
